import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, Search, Trash2, Edit, FileText, Check, Loader2, Sparkles, Mail, RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { fmtDate } from '../lib/dateUtils';

const STATUS_CONFIG = {
  da_assegnare: { label: 'Da assegnare', color: 'bg-amber-50 text-amber-700 border-amber-300' },
  assegnato: { label: 'Assegnato', color: 'bg-blue-50 text-blue-700 border-blue-300' },
  in_corso: { label: 'In corso', color: 'bg-indigo-50 text-indigo-700 border-indigo-300' },
  completato: { label: 'Completato', color: 'bg-green-50 text-green-700 border-green-300' },
  fatturato: { label: 'Fatturato', color: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
  annullato: { label: 'Annullato', color: 'bg-slate-100 text-slate-600 border-slate-300' },
};

const eur = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);
const fileHref = (url) => `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;

export default function ClientOrdersPage() {
  const { isRole } = useAuth();
  const canDelete = isRole('admin', 'qhse');
  const fileRef = useRef(null);
  const [orders, setOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState('__all__');
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [filterYear, setFilterYear] = useState('__all__');
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const isAdmin = isRole('admin');
  const [searchParams, setSearchParams] = useSearchParams();
  const [mail, setMail] = useState(null);
  const [showMail, setShowMail] = useState(false);
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  const [webappUrl, setWebappUrl] = useState('');
  const [webappMsg, setWebappMsg] = useState(null);

  const loadMail = useCallback(async () => {
    if (!isAdmin) return;
    try { setMail((await api.get('/client-orders/mail-settings')).data); } catch { /* ignore */ }
  }, [isAdmin]);
  useEffect(() => { loadMail(); }, [loadMail]);

  const openMail = async () => {
    setCopied(false);
    setWebappUrl(mail?.webapp_url || '');
    setWebappMsg(null);
    setShowMail(true);
    try { setToken((await api.post('/client-orders/mail-settings/token', {})).data.token); } catch { setToken(''); }
  };

  const rotateToken = async () => {
    if (!window.confirm('Generare una nuova chiave? Lo script gia installato smettera di funzionare finche non incolli quello nuovo.')) return;
    setToken((await api.post('/client-orders/mail-settings/token', { rotate: true })).data.token);
    setCopied(false);
    loadMail();
  };

  const scriptText = `const BASE_GESTIONALE = '${window.location.origin}/api';
const CHIAVE = '${token}';

// Ogni flusso legge le email di un mittente e invia i PDF allegati al gestionale.
// Installa questo stesso codice sulle caselle che ricevono le email (ordini: info@; report tornelli: g.valeri@ e g.bagnato@).
// "iniziale" e' la data da cui recuperare lo storico; poi lo script legge solo le email nuove.
const FLUSSI = [
  { mittente: 'order.distribution@eseitalia.it', percorso: '/client-orders/inbound', ricerca: 'has:attachment filename:pdf', iniziale: '2026-02-01' },
  { mittente: 'noreply@eseitalia.it', percorso: '/turnstile/inbound', ricerca: 'subject:Report has:attachment filename:pdf', iniziale: '2026-07-01' }
];

function importaOrdini() {
  const lock = LockService.getScriptLock();
  if (!lock.tryLock(5000)) return;
  try { esegui_(); } finally { lock.releaseLock(); }
}

function doGet(e) {
  if (!e || !e.parameter || e.parameter.k !== CHIAVE) return ContentService.createTextOutput('chiave non valida');
  importaOrdini();
  return ContentService.createTextOutput('ok');
}

function chiama_(percorso, opzioni) {
  opzioni.headers = { 'X-Inbound-Token': CHIAVE };
  opzioni.muteHttpExceptions = true;
  return UrlFetchApp.fetch(BASE_GESTIONALE + percorso, opzioni);
}

function esegui_() {
  const inizio = Date.now();
  FLUSSI.forEach(function (f, i) {
    try {
      importaFlusso_(f, i, inizio);
    } catch (e) {
      console.error('Flusso ' + f.mittente + ': ' + e);
    }
  });
}

// Legge solo le email comprese in una finestra di date (al massimo 15 giorni per volta, dalla piu' vecchia)
// e ricorda fino a dove e' arrivato: i giri successivi non rileggono la posta gia' elaborata.
function importaFlusso_(f, indice, inizio) {
  const props = PropertiesService.getScriptProperties();
  const chiaveDesde = 'desde_' + indice;
  const adesso = Math.floor(Date.now() / 1000);
  let desde = Number(props.getProperty(chiaveDesde)) || Math.floor(new Date(f.iniziale + 'T00:00:00Z').getTime() / 1000);
  const inDiretta = desde + 15 * 86400 >= adesso;
  const fine = inDiretta ? adesso + 86400 : desde + 15 * 86400;

  const messaggi = [];
  let start = 0;
  while (true) {
    const threads = GmailApp.search('from:' + f.mittente + ' ' + f.ricerca + ' after:' + desde + ' before:' + fine, start, 100);
    threads.forEach(function (t) {
      t.getMessages().forEach(function (m) {
        if (m.getDate().getTime() / 1000 >= desde - 3600) messaggi.push(m);
      });
    });
    if (threads.length < 100) break;
    start += 100;
  }
  messaggi.sort(function (a, b) { return a.getDate() - b.getDate(); });

  let ultimoOk = desde;
  let completo = true;
  if (messaggi.length) {
    const ids = messaggi.map(function (m) { return m.getHeader('Message-ID') || m.getId(); });
    const ctrl = chiama_(f.percorso + '/check', { method: 'post', contentType: 'application/json', payload: JSON.stringify({ ids: ids }) });
    if (ctrl.getResponseCode() !== 200) {
      console.error('Errore ' + ctrl.getResponseCode() + ': ' + ctrl.getContentText());
      return;
    }
    const nuovi = JSON.parse(ctrl.getContentText()).new;

    for (let i = 0; i < messaggi.length; i++) {
      const m = messaggi[i];
      if (Date.now() - inizio > 270000) { console.log('Tempo quasi scaduto, si riprende al prossimo giro'); completo = false; break; }
      if (nuovi.indexOf(ids[i]) !== -1) {
        const pdfs = m.getAttachments().filter(function (a) { return a.getName().toLowerCase().endsWith('.pdf'); });
        if (pdfs.length) {
          const payload = { message_id: ids[i], subject: m.getSubject(), from: m.getFrom(), date: m.getDate().toISOString() };
          pdfs.forEach(function (p, k) { payload['file' + k] = p.copyBlob(); });
          const r = chiama_(f.percorso, { method: 'post', payload: payload });
          if (r.getResponseCode() !== 200) {
            console.error('Errore ' + r.getResponseCode() + ': ' + r.getContentText());
            completo = false;
            break;
          }
        }
      }
      ultimoOk = Math.floor(m.getDate().getTime() / 1000);
    }
  }
  if (completo) {
    props.setProperty(chiaveDesde, String(inDiretta ? adesso - 3600 : fine));
  } else {
    props.setProperty(chiaveDesde, String(Math.max(desde, ultimoOk - 60)));
  }
}`;

  const copyScript = async () => {
    try { await navigator.clipboard.writeText(scriptText); setCopied(true); } catch { setCopied(false); }
  };

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterProject !== '__all__') params.project_id = filterProject;
      if (filterStatus !== '__all__') params.status = filterStatus;
      if (filterYear !== '__all__') params.year = filterYear;
      const [o, p] = await Promise.all([
        api.get('/client-orders', { params }),
        api.get('/projects').catch(() => ({ data: [] })),
      ]);
      setOrders(o.data || []);
      setProjects(Array.isArray(p.data) ? p.data : []);
    } finally {
      setLoading(false);
    }
  }, [filterProject, filterStatus, filterYear]);

  useEffect(() => { load(); }, [load]);

  const refreshFromMail = async (auto = false) => {
    try {
      const r = (await api.post('/client-orders/mail-refresh', null, { params: { auto } })).data;
      if (!r.started) {
        if (!auto) {
          setRefreshMsg(r.reason === 'not_configured'
            ? { ok: false, text: isAdmin ? 'Aggiornamento non ancora collegato: apri "Collega email" e completa l\'ultimo passaggio.' : 'Aggiornamento non ancora collegato: chiedi all\'amministratore.' }
            : { ok: true, text: 'Aggiornamento gia in corso o appena eseguito.' });
        }
        return;
      }
      setRefreshing(true);
      setRefreshMsg(null);
      let st = null;
      for (let i = 0; i < 40; i++) {
        await new Promise(res => setTimeout(res, 4000));
        st = (await api.get('/client-orders/mail-refresh/status')).data;
        if (!st.running) break;
      }
      await load();
      loadMail();
      if (st?.last_refresh_status === 'errore') setRefreshMsg({ ok: false, text: st.last_refresh_error || 'Aggiornamento non riuscito' });
      else if (!auto) setRefreshMsg({ ok: true, text: 'Elenco aggiornato dalla casella email.' });
    } catch (err) {
      if (!auto) setRefreshMsg({ ok: false, text: err.response?.data?.detail || 'Aggiornamento non riuscito' });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { refreshFromMail(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const saveWebapp = async () => {
    setWebappMsg(null);
    try {
      await api.put('/client-orders/mail-settings/webapp', { url: webappUrl });
      setWebappMsg({ ok: true, text: webappUrl ? 'Indirizzo salvato. Ora il pulsante "Aggiorna da email" funziona.' : 'Indirizzo rimosso.' });
      loadMail();
    } catch (err) {
      setWebappMsg({ ok: false, text: err.response?.data?.detail || 'Salvataggio non riuscito' });
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const r = await api.post('/client-orders/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(r.data.results || []);
      load();
    } catch (err) {
      setUploadResult([{ file: '', status: 'errore', detail: err.response?.data?.detail || 'Caricamento non riuscito' }]);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const assign = async (order, projectId) => {
    await api.put(`/client-orders/${order.id}`, { project_id: projectId });
    load();
  };

  const openEdit = (o) => {
    setForm({
      project_id: o.project_id || '', status: o.status, description: o.description || '', po_date: o.po_date || '',
      delivery_date: o.delivery_date || '', net_value: o.net_value ?? 0, contract_ref: o.contract_ref || '',
      requester: o.requester || '', network_number: o.network_number || '', notes: o.notes || '',
    });
    setEdit(o);
  };

  useEffect(() => {
    const id = searchParams.get('open');
    if (!id || loading) return;
    const o = orders.find(x => x.id === id);
    if (o) openEdit(o);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, orders]);

  const saveEdit = async () => {
    try {
      await api.put(`/client-orders/${edit.id}`, form);
      setEdit(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Eliminare l'ordine ${o.po_number}?`)) return;
    await api.delete(`/client-orders/${o.id}`);
    load();
  };

  const years = Array.from(new Set(orders.map(o => (o.po_date || '').slice(0, 4)).filter(Boolean))).sort().reverse();
  const q = search.trim().toLowerCase();
  const shown = orders.filter(o => !q || [o.po_number, o.description, o.project_name, o.contract_ref, o.requester].join(' ').toLowerCase().includes(q));
  const toAssign = orders.filter(o => !o.project_id && o.status !== 'annullato').length;
  const totalValue = shown.filter(o => o.status !== 'annullato').reduce((s, o) => s + (o.net_value || 0), 0);
  const sortedProjects = [...projects].sort((a, b) => (a.status === 'attivo' ? 0 : 1) - (b.status === 'attivo' ? 0 : 1) || (a.name || '').localeCompare(b.name || ''));

  return (
    <div className="space-y-5" data-testid="client-orders-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ordini Cliente</h1>
          <p className="text-sm text-slate-500">Ordini di acquisto (PO) ricevuti dai clienti, collegati ai progetti</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={openMail} data-testid="po-mail-settings-btn">
              <Mail size={16} className="mr-1" /> Collega email
              {mail?.last_received_at && <span className="ml-2 h-2 w-2 rounded-full bg-green-500" title="Importazione automatica attiva" />}
            </Button>
          )}
          <Button variant="outline" onClick={() => refreshFromMail(false)} disabled={refreshing} data-testid="po-refresh-btn">
            {refreshing ? <Loader2 size={16} className="mr-1 animate-spin" /> : <RefreshCw size={16} className="mr-1" />}
            {refreshing ? 'Aggiornamento...' : 'Aggiorna da email'}
          </Button>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={handleUpload} data-testid="po-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="po-upload-btn">
            {uploading ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Upload size={16} className="mr-1" />}
            Carica PO (PDF)
          </Button>
        </div>
      </div>

      {isAdmin && mail?.configured && (() => {
        const last = mail.last_check_at || mail.last_received_at;
        const fmt = (iso) => new Date(iso).toLocaleString('it-IT', { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        const stale = !last || (Date.now() - new Date(last).getTime()) > 60 * 60 * 1000;
        return stale ? (
          <p className="text-sm rounded-md border border-red-300 bg-red-50 text-red-700 px-3 py-2" data-testid="po-mail-warning">
            Lo script della casella email non si collega al gestionale {last ? `dal ${fmt(last)}` : 'da sempre'}: i nuovi ordini via email non vengono importati.
            {mail.webapp_url ? ' Premi "Aggiorna da email" per forzare il controllo.' : ' Apri "Collega email" e completa l\'ultimo passaggio (app web): poi "Aggiorna da email" funziona anche senza attivatore.'}
          </p>
        ) : (
          <p className="text-xs text-slate-500">
            Importazione automatica da email attiva - ultimo controllo dello script il {fmt(last)}
            {mail.last_received_at ? ` - ultima email importata il ${fmt(mail.last_received_at)}` : ''}
          </p>
        );
      })()}

      {refreshMsg && (
        <p className={`text-sm ${refreshMsg.ok ? 'text-green-700' : 'text-red-600'}`} data-testid="po-refresh-msg">{refreshMsg.text}</p>
      )}

      {uploadResult && (
        <Card className="border-slate-200">
          <CardContent className="py-3 space-y-1">
            {uploadResult.map((r, i) => (
              <p key={i} className="text-sm flex items-center gap-2">
                {r.status === 'creato' && <Check size={14} className="text-green-600" />}
                <span className="font-medium">{r.po_number || r.file}</span>
                <span className={r.status === 'errore' ? 'text-red-600' : r.status === 'duplicato' ? 'text-amber-600' : 'text-green-700'}>
                  {r.status === 'creato' ? (r.assigned ? 'importato e assegnato al progetto' : 'importato, da assegnare') : r.status === 'duplicato' ? 'gia presente, non duplicato' : r.detail}
                </span>
              </p>
            ))}
            <button className="text-xs text-slate-400 underline" onClick={() => setUploadResult(null)}>Chiudi</button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card><CardContent className="pt-4 pb-3"><p className="text-[10px] font-semibold uppercase text-slate-400">Ordini</p><p className="text-2xl font-black">{shown.length}</p></CardContent></Card>
        <Card className={toAssign > 0 ? 'border-amber-300' : ''}><CardContent className="pt-4 pb-3"><p className="text-[10px] font-semibold uppercase text-slate-400">Da assegnare</p><p className="text-2xl font-black text-amber-600">{toAssign}</p></CardContent></Card>
        <Card className="col-span-2 sm:col-span-1"><CardContent className="pt-4 pb-3"><p className="text-[10px] font-semibold uppercase text-slate-400">Valore netto</p><p className="text-2xl font-black">{eur(totalValue)}</p></CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Cerca per numero PO, descrizione, progetto, contratto" value={search} onChange={e => setSearch(e.target.value)} data-testid="po-search" />
        </div>
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="w-52"><SelectValue placeholder="Progetto" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutti i progetti</SelectItem>
            {sortedProjects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutti gli stati</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterYear} onValueChange={setFilterYear}>
          <SelectTrigger className="w-28"><SelectValue placeholder="Anno" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutti</SelectItem>
            {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>PO</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead>Progetto</TableHead>
                <TableHead className="text-right">Netto</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="w-28">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : shown.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Nessun ordine. Carica il PDF di un PO per iniziare.</TableCell></TableRow>
              ) : shown.map(o => (
                <TableRow key={o.id} data-testid={`po-row-${o.po_number}`}>
                  <TableCell className="font-mono text-sm whitespace-nowrap">{o.po_number}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(o.po_date)}</TableCell>
                  <TableCell className="text-sm min-w-[20rem] max-w-[40rem]">
                    <p className="font-medium" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={o.description || ''}>{o.description || '-'}</p>
                    {o.contract_ref && <p className="text-xs text-slate-500 truncate" title={`Contratto ${o.contract_ref}${o.requester ? ` - ${o.requester}` : ''}`}>Contratto {o.contract_ref}{o.requester ? ` - ${o.requester}` : ''}</p>}
                  </TableCell>
                  <TableCell className="text-sm min-w-[200px]">
                    {o.project_id ? (
                      <span className="font-medium">{o.project_name || '-'}</span>
                    ) : (
                      <div className="space-y-1">
                        {o.suggestions?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {o.suggestions.map(s => (
                              <button key={s.id} onClick={() => assign(o, s.id)} title={`${s.reason} (${Math.round(s.score * 100)}%) - clicca per assegnare`}
                                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100" data-testid={`po-suggest-${o.po_number}`}>
                                <Sparkles size={11} /> {s.name}
                              </button>
                            ))}
                          </div>
                        )}
                        <Select value="" onValueChange={(v) => assign(o, v)}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Assegna a progetto..." /></SelectTrigger>
                          <SelectContent>{sortedProjects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-right whitespace-nowrap tabular-nums">{eur(o.net_value)}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS_CONFIG[o.status]?.color}>{STATUS_CONFIG[o.status]?.label || o.status}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <a href={fileHref(o.file_url)} target="_blank" rel="noopener noreferrer" title="Apri PDF" className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-slate-100"><FileText size={15} /></a>
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Modifica" onClick={() => openEdit(o)}><Edit size={15} /></Button>
                      {canDelete && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" title="Elimina" onClick={() => remove(o)}><Trash2 size={15} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showMail} onOpenChange={setShowMail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Importa gli ordini dalla casella email</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-slate-500">
              Uno script installato sulla casella info@metalcovering.org controlla ogni 10 minuti le email di order.distribution@eseitalia.it,
              prende i PDF allegati e li invia al gestionale. Non serve nessuna password: lo script usa l'accesso Google gia attivo e
              comunica con il gestionale tramite una chiave segreta. Le email non vengono spostate ne segnate come lette.
            </p>
            <ol className="list-decimal pl-5 space-y-2">
              <li>Accedi a Google con l'account <b>info@metalcovering.org</b> e apri <a className="text-blue-600 underline" href="https://script.google.com" target="_blank" rel="noopener noreferrer">script.google.com</a>, poi <b>Nuovo progetto</b>. Lo stesso codice serve anche per i report dei tornelli Sarpom: va installato anche sulla casella che li riceve (g.bagnato@ o g.valeri@), con gli stessi passaggi.</li>
              <li>Cancella il codice presente e incolla il codice del riquadro in fondo a questa finestra (pulsante Copia). Premi l'icona del dischetto per salvare.</li>
              <li>In alto scegli la funzione <b>importaOrdini</b> e premi <b>Esegui</b>. Google chiede il permesso di leggere la posta e di collegarsi a un servizio esterno: autorizza (se compare "app non verificata", scegli Avanzate e Vai a progetto).</li>
              <li>A sinistra apri <b>Attivatori</b> (icona dell'orologio), poi <b>Aggiungi attivatore</b>: funzione <b>importaOrdini</b>, origine evento <b>Basato sul tempo</b>, <b>Timer in minuti</b>, ogni <b>10 minuti</b>. Salva.</li>
              <li><b>Per il pulsante "Aggiorna da email"</b> (consigliato, funziona anche se l'attivatore a tempo si ferma): in alto a destra premi <b>Distribuisci</b>, poi <b>Nuova distribuzione</b>, ingranaggio, <b>App web</b>. Imposta <b>Esegui come: Me</b> e <b>Chi ha accesso: Chiunque</b>, premi <b>Distribuisci</b>, autorizza se richiesto e copia l'<b>URL dell'app web</b> (finisce con /exec). Incollalo qui sotto.</li>
            </ol>
            <div className="space-y-1.5">
              <Label>URL dell'app web (uno per riga, uno per ogni casella dove hai installato lo script)</Label>
              <div className="flex gap-2">
                <Textarea rows={3} placeholder="https://script.google.com/macros/s/.../exec" value={webappUrl} onChange={e => setWebappUrl(e.target.value)} data-testid="mail-webapp-url" />
                <Button onClick={saveWebapp} data-testid="mail-webapp-save">Salva</Button>
              </div>
              {webappMsg && <p className={`text-xs ${webappMsg.ok ? 'text-green-700' : 'text-red-600'}`}>{webappMsg.text}</p>}
              <p className="text-xs text-slate-400">Quando cambi il codice dello script, ricordati di aggiornare anche la distribuzione: Distribuisci, Gestisci distribuzioni, matita, Versione: Nuova versione.</p>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Codice da incollare</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={rotateToken} data-testid="mail-rotate-btn">Nuova chiave</Button>
                  <Button size="sm" onClick={copyScript} disabled={!token} data-testid="mail-copy-btn">{copied ? 'Copiato' : 'Copia'}</Button>
                </div>
              </div>
              <textarea readOnly rows={9} value={token ? scriptText : 'Generazione della chiave...'} className="w-full rounded-md border border-slate-200 bg-slate-50 p-2 font-mono text-[11px]" onFocus={e => e.target.select()} data-testid="mail-script" />
              <p className="text-xs text-slate-400">Il codice contiene la chiave segreta: non condividerlo. Importa le email dal 1 febbraio 2026 in poi (la data e' nella riga DAL del codice). Con tanti ordini la prima esecuzione puo richiedere piu giri da 10 minuti: riprende da sola da dove si era fermata.</p>
            </div>
            {mail?.last_received_at && (
              <p className="text-green-700 text-xs">Collegamento attivo: ultima email ricevuta il {new Date(mail.last_received_at).toLocaleString('it-IT', { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { loadMail(); setShowMail(false); }}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Ordine {edit?.po_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Progetto</Label>
              <Select value={form.project_id || '__none__'} onValueChange={v => setForm({ ...form, project_id: v === '__none__' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessun progetto</SelectItem>
                  {sortedProjects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Descrizione</Label><Textarea rows={2} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Data ordine</Label><Input type="date" value={form.po_date || ''} onChange={e => setForm({ ...form, po_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Data consegna</Label><Input type="date" value={form.delivery_date || ''} onChange={e => setForm({ ...form, delivery_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Importo netto (EUR)</Label><Input type="number" step="0.01" value={form.net_value ?? 0} onChange={e => setForm({ ...form, net_value: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Contratto</Label><Input value={form.contract_ref || ''} onChange={e => setForm({ ...form, contract_ref: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Richiedente</Label><Input value={form.requester || ''} onChange={e => setForm({ ...form, requester: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>N. network</Label><Input value={form.network_number || ''} onChange={e => setForm({ ...form, network_number: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button>
            <Button onClick={saveEdit} data-testid="po-save-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
